import { z } from "zod";
import { PAID_PLAN_IDS, planIdSchema } from "../billing/plans.js";
import { LIMITS, emailSchema, inputValuesSchema } from "./common.js";
import { actionIdSchema, messageIdSchema, tenantIdSchema } from "./ids.js";
import { billLimitUsdSchema, tenantNameSchema } from "./tenant.js";

/** Bodies of the control API requests that have no other home. */

export const linkRequestSchema = z.strictObject({ email: emailSchema });
export const tokenBodySchema = z.strictObject({ token: z.string().min(16).max(128) });
export const tenantCreateSchema = z.strictObject({ name: tenantNameSchema });
export const tenantSwitchSchema = z.strictObject({ tenantId: tenantIdSchema });
export const tenantDeleteSchema = z.strictObject({ confirmName: z.string() });
export const memberInviteSchema = z.strictObject({ email: emailSchema });
export const apiKeyCreateSchema = z.strictObject({ label: z.string().trim().min(1).max(60) });
export const rejectBodySchema = z.strictObject({ reason: z.string().max(200).optional() });
/** Approve a waiting run. `inputs` replaces written (`compose`) values the member edited. */
export const approveBodySchema = z.strictObject({
  inputs: z.record(z.string(), z.string().trim().min(1).max(LIMITS.editedText)).optional(),
});
export type ApproveBody = z.infer<typeof approveBodySchema>;
export const actionTestSchema = z.strictObject({});

/**
 * A member's choice of a different action, with their typed `extract` values.
 * `needsApproval` parks the new run, with its written text, for approval.
 */
export const overrideBodySchema = z.strictObject({
  actionId: actionIdSchema,
  inputs: inputValuesSchema,
  needsApproval: z.boolean(),
  /** Whether the router sees this message and action as a past example. */
  addExample: z.boolean(),
});
export type OverrideBody = z.infer<typeof overrideBodySchema>;

/**
 * One action for many messages. It takes no typed values and adds no example,
 * so an override to Ignore is a dismiss.
 */
export const bulkOverrideBodySchema = z.strictObject({
  messageIds: z.array(messageIdSchema).min(1).max(LIMITS.bulkOverride),
  actionId: actionIdSchema,
  needsApproval: z.boolean(),
});
export type BulkOverrideBody = z.infer<typeof bulkOverrideBodySchema>;

/** Close an open follow-up, or reopen a closed one. */
export const followUpChangeSchema = z.strictObject({ state: z.enum(["open", "closed"]) });
export type FollowUpChange = z.infer<typeof followUpChangeSchema>;

export const sessionResponseSchema = z.strictObject({
  session: z.string(),
  tenants: z.array(z.strictObject({ id: tenantIdSchema, name: z.string() })),
  active: tenantIdSchema.nullable(),
});
export type SessionResponse = z.infer<typeof sessionResponseSchema>;

export const API_ERROR_CODES = [
  "bad_request",
  "unauthorized",
  "forbidden",
  "not_found",
  "conflict",
  "gone",
  "rate_limited",
  "internal",
] as const;
export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

export const apiErrorSchema = z.strictObject({
  error: z.strictObject({ code: z.enum(API_ERROR_CODES), message: z.string() }),
});

export const checkoutBodySchema = z.strictObject({ plan: z.enum(PAID_PLAN_IDS) });
export const planChangeBodySchema = z.strictObject({ plan: planIdSchema });
export const billLimitBodySchema = z.strictObject({ billLimitUsd: billLimitUsdSchema });
